import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { HTTP_STATUS, devError } from '../lib/constants';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (!cancelled) {
          setUser(data.user);
          setWorkspace(data.workspace);
        }
      } catch (err) {
        if (err?.response?.status !== HTTP_STATUS.UNAUTHORIZED) {
          devError('auth/me failed', err);
        }
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const applyAuthResponse = useCallback((data) => {
    setUser(data.user);
    setWorkspace(data.workspace || null);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    applyAuthResponse(data);
    return data.user;
  }, [applyAuthResponse]);

  const registerCreateWorkspace = useCallback(async (payload) => {
    const { data } = await api.post('/auth/register', { mode: 'create_workspace', ...payload });
    applyAuthResponse(data);
    return data.user;
  }, [applyAuthResponse]);

  const registerWithInvite = useCallback(async (payload) => {
    const { data } = await api.post('/auth/register', { mode: 'invite', ...payload });
    applyAuthResponse(data);
    return data.user;
  }, [applyAuthResponse]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      devError('logout failed', err);
    }
    setUser(false);
    setWorkspace(null);
  }, []);

  const value = useMemo(() => ({
    user, workspace, loading,
    login, registerCreateWorkspace, registerWithInvite, logout,
    setUser, setWorkspace,
  }), [user, workspace, loading, login, registerCreateWorkspace, registerWithInvite, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
